import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { alertSentryOnAPIError, logToSentry } from '@utils/sentry'
import { IApiFieldError, INotification } from '../store/data'

const languages = ['cs_CZ', 'de', 'ru', 'en']
const MAX_NETWORK_RETRIES = 2
const NETWORK_RETRY_DELAY = 500
const RETRYABLE_TRANSPORT_ERROR_CODES = ['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT']

interface RetryConfig extends InternalAxiosRequestConfig {
  __networkRetryCount?: number
}

const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds))

const isIdempotentRequest = (config?: InternalAxiosRequestConfig) =>
  ['get', 'head', 'options'].includes(config?.method?.toLowerCase() ?? '')

const isBrowserRequestAbort = (error: AxiosError) =>
  error.code === 'ECONNABORTED' && error.message === 'Request aborted'

const isRetryableNetworkError = (error: AxiosError) =>
  !axios.isCancel(error) &&
  !isBrowserRequestAbort(error) &&
  isIdempotentRequest(error.config) &&
  RETRYABLE_TRANSPORT_ERROR_CODES.includes(error.code ?? '')

const instance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    Accept: 'application/json',
    'Accept-Language': languages.join(', '),
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
    'Content-Type': 'application/json',
  },
})

instance.interceptors.request.use((config) => {
  logToSentry(`calling API end point ${config.baseURL}`, config)
  return config
})

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined

    if (!config || !isRetryableNetworkError(error)) {
      return Promise.reject(error)
    }

    config.__networkRetryCount = config.__networkRetryCount ?? 0

    if (config.__networkRetryCount >= MAX_NETWORK_RETRIES) {
      return Promise.reject(error)
    }

    config.__networkRetryCount += 1
    await delay(NETWORK_RETRY_DELAY * config.__networkRetryCount)

    return instance.request(config)
  }
)

export const isReportableApiError = (error: unknown) => {
  if (!(error instanceof AxiosError)) {
    return true
  }

  if (axios.isCancel(error) || error.code === 'ERR_NETWORK' || isBrowserRequestAbort(error)) {
    return false
  }

  return !error.response?.status || error.response.status >= 500
}

export const getApiErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (typeof error.message === 'string' && error.message.trim().startsWith('URGENT -')) {
      return error.message
    }

    const responseMessage = error.response?.data?.message ?? error.response?.data?.error?.message

    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage
    }

    if (Array.isArray(responseMessage) && responseMessage.length > 0) {
      return responseMessage.join(', ')
    }

    if (error.code === 'ERR_NETWORK') {
      return error.message
    }

    return error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unknown error'
}

export const getApiFieldErrors = (error: unknown): IApiFieldError[] => {
  if (!(error instanceof AxiosError)) {
    return []
  }

  const message = error.response?.data?.message ?? error.response?.data?.error?.message

  if (typeof message !== 'string') {
    return []
  }

  if (
    message === 'Requested guest1LoyaltyProgram is not assigned for visit type' ||
    message === 'Requested guest1LoyaltyProgram does not exist'
  ) {
    return [
      {
        field: 'loyaltyPrograms.0.id',
        message,
        scope: 'services',
      },
    ]
  }

  if (
    message === 'Requested guest2LoyaltyProgram is not assigned for visit type' ||
    message === 'Requested guest2LoyaltyProgram does not exist' ||
    message === 'guest2LoyaltyProgram is not allowed for single bed room type'
  ) {
    return [
      {
        field: 'loyaltyPrograms.1.id',
        message,
        scope: 'services',
      },
    ]
  }

  return []
}

export const handleApiError = (
  error: unknown,
  addNotification: (notification: INotification) => void
) => {
  let isNetworkError = false
  const errorMessage = getApiErrorMessage(error)

  if (error instanceof AxiosError) {
    if (error.code === 'ERR_NETWORK') {
      isNetworkError = true
    }
  }

  addNotification({
    closable: !isNetworkError,
    isNetworkError,
    message: errorMessage,
    timestamp: new Date().getTime(),
  })

  if (error instanceof AxiosError && isReportableApiError(error)) {
    alertSentryOnAPIError(error)
  }
}

export default instance
